/**
 * グリッドからの編集をソース .pix テキストに反映するユーティリティ。
 *
 * 戦略:
 *  - メッセージは「該当する Speech ブロック行範囲を新内容で置換」
 *  - BGM/BG など状態変化は「該当行の直前に @command 1 行を挿入」
 *  - 既存コメントや書式は基本的に保存される
 */

/** 行末・行頭の改行を保ったまま安全に分割 */
function splitLines(source: string): { lines: string[]; eol: string } {
  const eol = source.includes("\r\n") ? "\r\n" : "\n";
  return { lines: source.split(/\r?\n/), eol };
}

function joinLines(lines: string[], eol: string): string {
  return lines.join(eol);
}

/**
 * 発話(または地の文)ブロックの行範囲を探す。
 * startLine(1-based) を起点に、空行・別ブロック開始・コマンドが出るまで継続行を含める。
 */
export function findSpeechBlockRange(
  source: string,
  startLine: number,
): { start: number; end: number } {
  const { lines } = splitLines(source);
  const startIdx = startLine - 1;
  if (startIdx < 0 || startIdx >= lines.length) return { start: startLine, end: startLine };
  let endIdx = startIdx;
  while (endIdx + 1 < lines.length) {
    const next = lines[endIdx + 1]!;
    const trimmed = next.trim();
    if (trimmed === "") break;
    if (trimmed.startsWith("@")) break;
    if (trimmed.startsWith("## ")) break;
    if (trimmed.startsWith("> ")) break;
    if (/^\s*-\s+\[/.test(next)) break;
    if (trimmed.startsWith(";;") || trimmed.startsWith("//")) break;
    if (/^[^@>:;/\-\s][^:]*:\s+/.test(trimmed)) break;
    endIdx++;
  }
  return { start: startIdx + 1, end: endIdx + 1 };
}

/** メッセージ/ナレーションブロックを置換 */
export function replaceSpeechBlock(
  source: string,
  startLine: number,
  speaker: string | null,
  text: string,
): string {
  const { lines, eol } = splitLines(source);
  const range = findSpeechBlockRange(source, startLine);
  const startIdx = range.start - 1;
  const endIdx = range.end - 1;

  const textLines = text.split(/\r?\n/);
  const formatted: string[] =
    speaker === null || speaker.trim() === ""
      ? // ナレーション
        textLines.map((l, i) => (i === 0 ? `> ${l}` : l))
      : // 発話
        textLines.map((l, i) => (i === 0 ? `${speaker}: ${l}` : l));

  return joinLines(
    [...lines.slice(0, startIdx), ...formatted, ...lines.slice(endIdx + 1)],
    eol,
  );
}

/** 指定行の直前に 1 行挿入 */
export function insertLineBefore(
  source: string,
  lineNum: number,
  newLine: string,
): string {
  const { lines, eol } = splitLines(source);
  const idx = Math.max(0, Math.min(lines.length, lineNum - 1));
  return joinLines([...lines.slice(0, idx), newLine, ...lines.slice(idx)], eol);
}

/** 連続する選択肢行(- [...](...)の連続) の終端を探す */
export function findChoiceBlockRange(
  source: string,
  startLine: number,
): { start: number; end: number } {
  const { lines } = splitLines(source);
  const startIdx = startLine - 1;
  let endIdx = startIdx;
  while (endIdx + 1 < lines.length) {
    if (/^\s*-\s+\[/.test(lines[endIdx + 1] ?? "")) {
      endIdx++;
    } else {
      break;
    }
  }
  return { start: startIdx + 1, end: endIdx + 1 };
}

export interface ChoiceOption {
  text: string;
  target: string;
  condition?: string;
}

/** 選択肢ブロックを置換 */
export function replaceChoiceBlock(
  source: string,
  startLine: number,
  choices: ChoiceOption[],
): string {
  const { lines, eol } = splitLines(source);
  const range = findChoiceBlockRange(source, startLine);
  const startIdx = range.start - 1;
  const endIdx = range.end - 1;

  const newLines = choices.map((c) => {
    const cond = c.condition && c.condition.trim() ? ` @if ${c.condition.trim()}` : "";
    return `- [${c.text}](${c.target})${cond}`;
  });

  return joinLines(
    [...lines.slice(0, startIdx), ...newLines, ...lines.slice(endIdx + 1)],
    eol,
  );
}

/** 行範囲を削除(両端 inclusive、1-based) */
export function deleteLines(
  source: string,
  startLine: number,
  endLine: number,
): string {
  const { lines, eol } = splitLines(source);
  const startIdx = Math.max(0, startLine - 1);
  const endIdx = Math.min(lines.length, endLine);
  return joinLines([...lines.slice(0, startIdx), ...lines.slice(endIdx)], eol);
}

/**
 * 行範囲 [fromStart..fromEnd] を「insertBeforeLine の手前」へ移動する。
 *  - 1-based、inclusive
 *  - insertBeforeLine がブロック内部なら no-op
 *  - 元のインデント・改行は保たれる
 */
export function moveBlock(
  source: string,
  fromStart: number,
  fromEnd: number,
  insertBeforeLine: number,
): string {
  if (fromEnd < fromStart) return source;
  if (insertBeforeLine >= fromStart && insertBeforeLine <= fromEnd + 1) return source;
  const { lines, eol } = splitLines(source);
  const block = lines.slice(fromStart - 1, fromEnd);
  if (block.length === 0) return source;
  const without = [...lines.slice(0, fromStart - 1), ...lines.slice(fromEnd)];

  let idx: number;
  if (insertBeforeLine <= fromStart) {
    idx = insertBeforeLine - 1;
  } else {
    idx = insertBeforeLine - 1 - block.length;
  }
  idx = Math.max(0, Math.min(without.length, idx));
  return joinLines([...without.slice(0, idx), ...block, ...without.slice(idx)], eol);
}

/** 1 行を新しい内容で置換(先頭インデントは維持) */
export function replaceLine(
  source: string,
  lineNum: number,
  newContent: string,
): string {
  const { lines, eol } = splitLines(source);
  const idx = lineNum - 1;
  if (idx < 0 || idx >= lines.length) return source;
  const original = lines[idx] ?? "";
  const leadingWs = original.match(/^\s*/)?.[0] ?? "";
  return joinLines(
    [...lines.slice(0, idx), leadingWs + newContent, ...lines.slice(idx + 1)],
    eol,
  );
}
